import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Paper } from '@mui/material';
import { Add } from '@mui/icons-material';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import PageTabNavigation from 'src/shared/components/navigations/pageTabNavigation';
import DataGridWrapper from 'src/shared/components/organisms/dataGridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpPageCategory } from '../models/helpPageCategory';
import { getAllHelpPageCategories, updateHelpPageCategory } from '../services/helpPageCategoryService';
import { HelpPageCategoryTree } from '../components/helpPageCategoryTree';
import { helpArticlesPageTabs } from '../functions/helpArticlesPageTabs';

export const HelpArticleCategoriesPage = () => {
  const { t } = useTranslation(['helpArticles']);
  const navigate = useNavigate();
  const isMounted = useIsMounted();

  const [categories, setCategories] = useState<HelpPageCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refetch, setRefetch] = useState<boolean>(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllHelpPageCategories();
      if (isMounted()) {
        setCategories(result.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoading(false);
        setRefetch(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    if (isMounted()) fetchCategories();
  }, [fetchCategories, isMounted]);

  useEffect(() => {
    if (isMounted() && refetch) fetchCategories();
  }, [fetchCategories, isMounted, refetch]);

  // Byter plats på kategorin och dess syskon genom att byta sortOrder med varandra.
  const handleMove = async (category: HelpPageCategory, direction: -1 | 1) => {
    const siblings = categories.filter((c) => c.parentId === category.parentId).sort((a, b) => a.sortOrder - b.sortOrder);
    const swapWith = siblings[siblings.findIndex((c) => c.id === category.id) + direction];
    if (!swapWith) return;

    setLoading(true);
    try {
      await updateHelpPageCategory({ ...category, sortOrder: swapWith.sortOrder });
      await updateHelpPageCategory({ ...swapWith, sortOrder: category.sortOrder });
      if (isMounted()) setRefetch(true);
    } catch (e) {
      // Trädet hämtas om även vid fel, eftersom det första platsbytet kan vara sparat.
      ConsoleHelper.log(e);
      if (isMounted()) setRefetch(true);
    }
  };

  const handleEdit = (category: HelpPageCategory) => {
    navigate(`${RoutesEnum.HelpArticleCategoryEdit}/${category.id}`);
  };

  const handleCreate = () => {
    navigate(RoutesEnum.HelpArticleCategoryCreate);
  };

  return (
    <GridWrapper>
      <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <MainContentHeader title={t('helpArticles:title')} />
      </Grid>

      <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={handleCreate} variant='contained' color='primary' startIcon={<Add fontSize='small' />} disableElevation>
          {t('helpArticles:categories.createCategory')}
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <PageTabNavigation pageTabs={helpArticlesPageTabs(t)} />
          <DataGridWrapper loading={loading}>
            <HelpPageCategoryTree categories={categories} onEdit={handleEdit} onMove={handleMove} />
          </DataGridWrapper>
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
